import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { JSX } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { Clock } from 'lucide-react';
import { useToast } from '@admin-platform/ui/components';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../Card/Card';
import { Label } from '../../Inputs/Label';
import { SingleSelect } from '../../Inputs/SingleSelect';

import styles from './TimezoneSettings.module.css';

import UPDATE_MY_TIMEZONE from '../mutations/updateMyTimezone.mutation.graphql';
import type {
  UpdateMyTimezoneMutation,
  UpdateMyTimezoneMutationVariables,
} from '../mutations/updateMyTimezone.mutation.graphql';
import MY_TIMEZONE from '../queries/myTimezone.query.graphql';
import type { MyTimezoneQuery } from '../queries/myTimezone.query.graphql';

/**
 * Every IANA zone this browser knows, for the picker.
 *
 * `Intl.supportedValuesOf` is the runtime's own list, so it cannot drift from
 * what the server will accept (it validates the same way). Older browsers
 * without it fall back to just the detected zone — one correct option beats a
 * hard-coded list that goes stale.
 */
function supportedTimezones(detected: string): string[] {
  try {
    const supported = Intl.supportedValuesOf?.('timeZone');
    if (supported?.length) return supported;
  } catch {
    // fall through
  }
  return detected ? [detected] : ['UTC'];
}

function detectBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

/**
 * The account page's timezone control.
 *
 * Scheduled prompts (weekly check-ins, digests) are sent at the recipient's
 * LOCAL hour, so this value decides when someone is messaged. It is captured
 * from the browser the first time the page loads with none stored, because
 * asking people to set a timezone they never think about is how half a company
 * ends up being prompted at 3am.
 */
export default function TimezoneSettings(): JSX.Element {
  const toast = useToast();
  const detected = useMemo(detectBrowserTimezone, []);
  const zones = useMemo(() => supportedTimezones(detected), [detected]);

  const { data, loading } = useQuery<MyTimezoneQuery>(MY_TIMEZONE, {
    fetchPolicy: 'cache-and-network',
  });
  const [updateTimezone, { loading: saving }] = useMutation<
    UpdateMyTimezoneMutation,
    UpdateMyTimezoneMutationVariables
  >(UPDATE_MY_TIMEZONE, { refetchQueries: [{ query: MY_TIMEZONE }] });

  const stored = data?.me?.timezone ?? null;

  // The auto-capture must fire once, not once per render and not again after
  // the user deliberately picks something else. A ref rather than state: it
  // guards an effect, and re-rendering on it would defeat the point.
  const autoCaptureAttempted = useRef(false);

  const save = useCallback(
    async (zone: string, silent: boolean) => {
      try {
        await updateTimezone({ variables: { timezone: zone } });
        if (!silent) toast(`Timezone set to ${zone}`, 'success');
      } catch (err) {
        // A silent auto-capture that fails must stay silent: the person did
        // not ask for it, and the fallback (the tenant's zone) still works.
        if (!silent) {
          toast(err instanceof Error ? err.message : 'Could not save your timezone.', 'error');
        }
      }
    },
    [toast, updateTimezone],
  );

  useEffect(() => {
    if (loading || autoCaptureAttempted.current) return;
    if (stored || !detected) return;
    autoCaptureAttempted.current = true;
    void save(detected, true);
  }, [loading, stored, detected, save]);

  const options = useMemo(
    () => zones.map((zone) => ({ label: zone.replace(/_/g, ' '), value: zone })),
    [zones],
  );

  // Show the detected zone while the first auto-capture is in flight, so the
  // field is never blank for someone who has simply never set one.
  const shown = stored ?? detected ?? '';

  return (
    <Card noHover>
      <CardHeader>
        <div className={styles.Row}>
          <span aria-hidden="true">
            <Clock />
          </span>
          <div>
            <CardTitle>Timezone</CardTitle>
            <CardDescription>
              Scheduled reminders arrive at this zone&apos;s local time.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className={styles.Body}>
        <div className={styles.Row}>
          <div className={styles.Select}>
            <Label htmlFor="account-timezone">Your timezone</Label>
            <SingleSelect
              id="account-timezone"
              options={options}
              value={shown}
              onChange={(value) => void save(String(value), false)}
              placeholder={loading ? 'Loading…' : 'Select a timezone'}
              searchable
              searchPlaceholder="Search timezones"
              disabled={saving}
              loading={loading}
            />
          </div>
        </div>
        <p className={styles.Hint}>
          {stored
            ? 'Detected from your browser the first time you signed in. Change it if you work elsewhere.'
            : 'We will use your browser’s zone until you choose one.'}
        </p>
      </CardContent>
    </Card>
  );
}
